import React, {
  useState,
  useCallback,
  useMemo,
  useContext,
  useLayoutEffect,
  SetStateAction,
  Dispatch
} from 'react';
import { v4 as uuidv4 } from 'uuid';
import ReactFlow, {
  applyNodeChanges,
  applyEdgeChanges,
  OnNodesChange,
  OnEdgesChange,
  Controls,
  ReactFlowInstance,
  Edge,
  Node,
  OnConnect,
  addEdge,
  Background,
  NodeSelectionChange,
  ConnectionLineType,
  MarkerType
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Loader2, Lock } from 'lucide-react';
import CustomTooltip from '../../../components/CustomTooltip/CustomTooltip';
import BotResponseTreeNode from '../../../types/BotBuilder/BotResponseTreeNode';
import IntentTreeNode from '../../../types/BotBuilder/IntentTreeNode';
import MessageBlockTreeNode from '../../../types/BotBuilder/MessageBlockTreeNode';
import ResponseNode2 from './ResponseNode/ResponseNode';
import ResponseCardsTreeNode from '../../../types/BotBuilder/ResponseCardsTreeNode';
import IntentNode2 from './IntentNode/IntentNode';
import Intent from '../../../types/BotBuilder/Intent';
import ResponseCardNode from './ResponseCardNode/ResponseCardNode';
import GoToIntentNode from './GoToIntentNode/GoToIntentNode';
import GoToIntentTreeNode from '../../../types/BotBuilder/GoToIntentTreeNode';
import CollectVariableNode from './CollectVariableNode/CollectVariableNode';
import CollectVariableTreeNode from '../../../types/BotBuilder/CollectVariableTreeNode';
import NodePalette from './NodePalette/NodePalette';
import BotBuilderContext from '../../../context/BotBuilderContext/BotBuilderContext';
import PreviewModeContext from '../../../context/PreviewModeContext/PreviewModeContext';
import ConditionalTreeNode from '../../../types/BotBuilder/ConditionalTreeNode';
import ConditionalNode from './ConditionalNode/ConditionalNode';
import SlotValueType from '../../../types/BotBuilder/SlotValueType';
import ComparisonOperator from '../../../types/BotBuilder/ComparisonOperator';
import MessageBlockContent from '../../../types/BotBuilder/MessageBlockContent';
import useKeyboardShortcuts from '../../../hooks/useKeyboardShortcuts';
import useParentHighlighting from '../../../hooks/useParentHighlighting';
import KeyboardShortcuts from '../KeyboardShortcuts/KeyboardShortcuts';
import GenerativeAiTreeNode from '../../../types/BotBuilder/GenerativeAiTreeNode';
import GenerativeAiNode from './GenerativeAiNode/GenerativeAiNode';
import CalculationTreeNode from '../../../types/BotBuilder/CalculationTreeNode';
import CalculationNode from './CalculationNode/CalculationNode';
import SendEmailTreeNode from '../../../types/BotBuilder/SendEmailTreeNode';
import SendSmsTreeNode from '../../../types/BotBuilder/SendSmsTreeNode';
import DeleteVariableTreeNode from '../../../types/BotBuilder/DeleteVariableTreeNode';
import CodeBlockTreeNode from '../../../types/BotBuilder/CodeBlockTreeNode';
import LlmTreeNode from '../../../types/BotBuilder/LlmTreeNode';
import DoNothingTreeNode from '../../../types/BotBuilder/DoNothingTreeNode';
import { DEFAULT_AI_MODEL } from '../../../types/RagAiModel';
import SendEmailNode from './SendEmailNode/SendEmailNode';
import SendSmsNode from './SendSmsNode/SendSmsNode';
import DeleteVariableNode from './DeleteVariableNode/DeleteVariableNode';
import CodeBlockNode from './CodeBlockNode/CodeBlockNode';
import LlmNode from './LlmNode/LlmNode';
import DoNothingNode from './DoNothingNode/DoNothingNode';

interface BotBuilderProps {
  intent: Intent | null;
  onSelectedNodeChange: (selectedNode: BotResponseTreeNode | null) => void;
  onMessageBlockEditIndexChange: (index: number | null) => void;
  onNodeUpdate: (node: BotResponseTreeNode) => void;
  newIntentIsLoading: boolean;
  setIntentHasChanged: Dispatch<SetStateAction<boolean>>;
  onNodeDelete: (node: BotResponseTreeNode) => void;
  openErrorSnackbar: (message: string) => void;
  isLocked?: boolean;
}

interface IntentNodeAndFlowNode {
  id: string;
  intentNode: BotResponseTreeNode;
  flowNode: Node;
}

function BotBuilder({
  intent,
  onSelectedNodeChange,
  onMessageBlockEditIndexChange,
  onNodeUpdate,
  newIntentIsLoading,
  setIntentHasChanged,
  onNodeDelete,
  openErrorSnackbar,
  isLocked = false
}: BotBuilderProps): React.ReactElement {
  const [reactFlowInstance, setReactFlowInstance] =
    useState<ReactFlowInstance | null>(null);
  const [nodes, setNodes] = useState<Node[]>([]);
  const [edges, setEdges] = useState<Edge[]>([]);
  const [intentAndFlowNodes, setIntentAndFlowNodes] = useState<
    IntentNodeAndFlowNode[]
  >([]);
  const [selectedNode, setSelectedNode] = useState<BotResponseTreeNode | null>(
    null
  );
  const [isFittingView, setIsFittingView] = useState(false);
  const previousIntentIdRef = React.useRef<number | null>(null);
  const isFittingViewInProgressRef = React.useRef<boolean>(false);
  const { addNode, unconnectedNodes, removeNode } =
    useContext(BotBuilderContext);
  const { setIsPreviewMode } = useContext(PreviewModeContext);

  const {
    highlightParent,
    highlightChild,
    resetHighlighting,
    reapplyHighlighting
  } = useParentHighlighting({
    setNodes,
    setEdges
  });

  const slotValueClickRef = React.useRef<
    (nodeId: string, handleId: string) => void
  >(() => {});

  const onSlotValueClick = useCallback((nodeId: string, handleId: string) => {
    slotValueClickRef.current(nodeId, handleId);
  }, []);

  const highlightRef = React.useRef<(nodeId: string) => void>(() => {});

  const onHighlight = useCallback((nodeId: string) => {
    highlightRef.current(nodeId);
  }, []);

  const nodeTypes = useMemo(
    () => ({
      intent: IntentNode2,
      response: ResponseNode2,
      responseCard: ResponseCardNode,
      goToIntent: GoToIntentNode,
      collectVariable: CollectVariableNode,
      conditional: ConditionalNode,
      generativeAi: GenerativeAiNode,
      calculation: CalculationNode,
      sendEmail: SendEmailNode,
      sendSms: SendSmsNode,
      deleteVariable: DeleteVariableNode,
      codeBlock: CodeBlockNode,
      llm: LlmNode,
      doNothing: DoNothingNode
    }),
    []
  );

  const getNodeType = (content: any): string => {
    if (content instanceof IntentTreeNode) return 'intent';
    if (content instanceof MessageBlockTreeNode) return 'response';
    if (content instanceof ResponseCardsTreeNode) return 'responseCard';
    if (content instanceof GoToIntentTreeNode) return 'goToIntent';
    if (content instanceof ConditionalTreeNode) return 'conditional';
    if (content instanceof GenerativeAiTreeNode) return 'generativeAi';
    if (content instanceof CalculationTreeNode) return 'calculation';
    if (content instanceof SendEmailTreeNode) return 'sendEmail';
    if (content instanceof SendSmsTreeNode) return 'sendSms';
    if (content instanceof DeleteVariableTreeNode) return 'deleteVariable';
    if (content instanceof CodeBlockTreeNode) return 'codeBlock';
    if (content instanceof LlmTreeNode) return 'llm';
    if (content instanceof DoNothingTreeNode) return 'doNothing';
    return 'collectVariable';
  };

  const isPartOfFallbackPath = (
    sourceNode: BotResponseTreeNode,
    targetNode: BotResponseTreeNode
  ): boolean => {
    if (
      sourceNode.content instanceof CollectVariableTreeNode &&
      sourceNode.content.fallbackStep &&
      sourceNode.content.fallbackStep.id === targetNode.id
    ) {
      return true;
    }

    if (
      sourceNode.content instanceof MessageBlockTreeNode &&
      targetNode.content instanceof CollectVariableTreeNode
    ) {
      return intentAndFlowNodes
        .map((n) => n.intentNode)
        .some(
          (node) =>
            node.content instanceof CollectVariableTreeNode &&
            node.content.fallbackStep &&
            node.content.fallbackStep.id === sourceNode.id &&
            targetNode.id === node.id
        );
    }

    return false;
  };

  const addNextStepsToQueue = useCallback(
    (currentNode: BotResponseTreeNode, queue: BotResponseTreeNode[]): void => {
      const { content } = currentNode;
      const pushIfDefined = (
        ...steps: (BotResponseTreeNode | null | undefined)[]
      ): void => {
        steps.forEach((step) => {
          if (step) queue.push(step);
        });
      };

      if (
        content instanceof IntentTreeNode ||
        content instanceof MessageBlockTreeNode ||
        content instanceof GenerativeAiTreeNode ||
        content instanceof CalculationTreeNode ||
        content instanceof SendEmailTreeNode ||
        content instanceof SendSmsTreeNode ||
        content instanceof DeleteVariableTreeNode ||
        content instanceof LlmTreeNode
      ) {
        pushIfDefined(content.nextStep);
      } else if (content instanceof CodeBlockTreeNode) {
        pushIfDefined(content.nextStep, content.nextStepOnError);
      } else if (content instanceof ResponseCardsTreeNode) {
        content.items.forEach((item) => pushIfDefined(item.nextStep));
      } else if (content instanceof CollectVariableTreeNode) {
        pushIfDefined(content.fallbackStep, content.slotProvidedStep);
        content.slotConditionals.forEach((c) => pushIfDefined(c.nextStep));
      } else if (content instanceof ConditionalTreeNode) {
        pushIfDefined(content.trueStep, content.falseStep);
      }
    },
    []
  );

  const isNodeReachableFromRoot = useCallback(
    (nodeId: string, rootNode: BotResponseTreeNode | null): boolean => {
      if (!rootNode || !nodeId) return false;
      if (rootNode.id === nodeId) return true;

      const visited = new Set<string>();
      const queue: BotResponseTreeNode[] = [rootNode];

      while (queue.length > 0) {
        const currentNode = queue.shift()!;

        if (visited.has(currentNode.id)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        visited.add(currentNode.id);

        if (currentNode.id === nodeId) {
          return true;
        }

        addNextStepsToQueue(currentNode, queue);
      }

      return false;
    },
    [addNextStepsToQueue]
  );

  const promoteOrphansToUnconnected = useCallback(() => {
    const rootNode = intentAndFlowNodes.find(
      (n) => n.intentNode.content instanceof IntentTreeNode
    )?.intentNode;

    if (!rootNode) return;

    intentAndFlowNodes.forEach(({ intentNode }) => {
      if (
        intentNode.id !== rootNode.id &&
        !isNodeReachableFromRoot(intentNode.id, rootNode) &&
        !unconnectedNodes.some((u) => u.id === intentNode.id)
      ) {
        addNode(intentNode);
      }
    });
  }, [intentAndFlowNodes, unconnectedNodes, addNode, isNodeReachableFromRoot]);

  const createEdge = (
    source: BotResponseTreeNode,
    target: BotResponseTreeNode,
    sourceHandle?: string,
    isFallback?: boolean
  ): Edge => {
    // Check if this edge is part of a fallback path (either directly or indirectly)
    const fallbackEdge = isFallback || isPartOfFallbackPath(source, target);

    return {
      id: sourceHandle
        ? `${source.id}-${sourceHandle}->${target.id}`
        : `${source.id}->${target.id}`,
      source: source.id,
      target: target.id,
      ...(sourceHandle && { sourceHandle }),
      type: 'smoothstep',
      markerEnd: {
        type: MarkerType.Arrow,
        width: 20,
        height: 20,
        color: fallbackEdge ? 'rgb(5, 125, 153)' : '#b1b1b7'
      },
      className: fallbackEdge ? 'fallback-edge' : 'default-edge',
      style: {
        stroke: fallbackEdge ? 'rgb(5, 125, 153)' : '#b1b1b7',
        strokeWidth: 1.5
      }
    };
  };

  const setMessageBlockEditIndex = (index: number): void => {
    onMessageBlockEditIndexChange(index);
  };

  const createNode = (
    node: BotResponseTreeNode,
    type: string,
    data: any
  ): Node => ({
    id: node.id,
    type,
    position: { x: node.xPosition, y: node.yPosition },
    data
  });

  useLayoutEffect(() => {
    const nodeMap = new Map<string, Node>();
    const intentAndFlowMap = new Map<string, IntentNodeAndFlowNode>();
    const edgeMap = new Map<string, Edge>();

    if (intent) {
      // Traverse the tree and add the nodes and edges
      const nodesToAdd: BotResponseTreeNode[] = [intent.rootNode];
      while (nodesToAdd.length > 0) {
        const currentTreeNode = nodesToAdd.shift()!;

        // If we've already processed this node, skip adding it again
        if (nodeMap.has(currentTreeNode.id)) {
          // eslint-disable-next-line no-continue
          continue;
        }

        let nodeType: string;
        let data: any = {
          node: currentTreeNode,
          onNodeUpdate,
          onNodeDelete,
          isLocked
        };
        if (currentTreeNode.content instanceof IntentTreeNode) {
          nodeType = 'intent';
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof GenerativeAiTreeNode) {
          nodeType = 'generativeAi';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof CalculationTreeNode) {
          nodeType = 'calculation';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof SendEmailTreeNode) {
          nodeType = 'sendEmail';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof SendSmsTreeNode) {
          nodeType = 'sendSms';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof DeleteVariableTreeNode) {
          nodeType = 'deleteVariable';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof LlmTreeNode) {
          nodeType = 'llm';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof CodeBlockTreeNode) {
          nodeType = 'codeBlock';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            onOutcomeClick: onSlotValueClick
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}-success->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(
                currentTreeNode,
                currentTreeNode.content.nextStep,
                `${currentTreeNode.id}-success-handle`
              )
            );
          }
          if (currentTreeNode.content.nextStepOnError) {
            nodesToAdd.push(currentTreeNode.content.nextStepOnError);
            const edgeId = `${currentTreeNode.id}-error->${currentTreeNode.content.nextStepOnError.id}`;
            edgeMap.set(
              edgeId,
              createEdge(
                currentTreeNode,
                currentTreeNode.content.nextStepOnError,
                `${currentTreeNode.id}-error-handle`,
                true
              )
            );
          }
        } else if (currentTreeNode.content instanceof MessageBlockTreeNode) {
          nodeType = 'response';
          data = {
            node: currentTreeNode,
            setMessageBlockEditIndex,
            onNodeUpdate,
            onNodeDelete,
            intentCategory: intent.category,
            onHighlight,
            isLocked
          };
          if (currentTreeNode.content.nextStep) {
            nodesToAdd.push(currentTreeNode.content.nextStep);
            const edgeId = `${currentTreeNode.id}->${currentTreeNode.content.nextStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(currentTreeNode, currentTreeNode.content.nextStep)
            );
          }
        } else if (currentTreeNode.content instanceof ResponseCardsTreeNode) {
          nodeType = 'responseCard';
          if (currentTreeNode.content.items) {
            currentTreeNode.content.items.forEach((item, index) => {
              if (item.nextStep) {
                nodesToAdd.push(item.nextStep);
                const edgeId = `${currentTreeNode.id}-response-card-${index}->${item.nextStep.id}`;
                edgeMap.set(
                  edgeId,
                  createEdge(
                    currentTreeNode,
                    item.nextStep,
                    `${currentTreeNode.id}-response-card-handle-${index}`
                  )
                );
              }
            });
          }
        } else if (currentTreeNode.content instanceof GoToIntentTreeNode) {
          nodeType = 'goToIntent';
        } else if (currentTreeNode.content instanceof DoNothingTreeNode) {
          nodeType = 'doNothing';
        } else if (currentTreeNode.content instanceof ConditionalTreeNode) {
          nodeType = 'conditional';
          const flowNode = createNode(currentTreeNode, nodeType, {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          });
          nodeMap.set(currentTreeNode.id, flowNode);
          intentAndFlowMap.set(currentTreeNode.id, {
            id: currentTreeNode.id,
            intentNode: currentTreeNode,
            flowNode
          });

          if (currentTreeNode.content.trueStep) {
            nodesToAdd.push(currentTreeNode.content.trueStep);
            const edgeId = `${currentTreeNode.id}-true->${currentTreeNode.content.trueStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(
                currentTreeNode,
                currentTreeNode.content.trueStep,
                `${currentTreeNode.id}-true-handle`
              )
            );
          }

          if (currentTreeNode.content.falseStep) {
            nodesToAdd.push(currentTreeNode.content.falseStep);
            const edgeId = `${currentTreeNode.id}-false->${currentTreeNode.content.falseStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(
                currentTreeNode,
                currentTreeNode.content.falseStep,
                `${currentTreeNode.id}-false-handle`
              )
            );
          }
        } else if (currentTreeNode.content instanceof CollectVariableTreeNode) {
          nodeType = 'collectVariable';
          data = {
            node: currentTreeNode,
            onNodeUpdate,
            onNodeDelete,
            onSlotValueClick,
            isLocked
          };
          if (currentTreeNode.content.fallbackStep) {
            nodesToAdd.push(currentTreeNode.content.fallbackStep);
            const edgeId = `${currentTreeNode.id}-fallback->${currentTreeNode.content.fallbackStep.id}`;
            edgeMap.set(
              edgeId,
              createEdge(
                currentTreeNode,
                currentTreeNode.content.fallbackStep,
                `${currentTreeNode.id}-fallback-handle`,
                true
              )
            );
          }

          if (currentTreeNode.content.slotProvidedStep) {
            nodesToAdd.push(currentTreeNode.content.slotProvidedStep);
            const providedEdgeId = `${currentTreeNode.id}-slot-provided->${currentTreeNode.content.slotProvidedStep.id}`;
            edgeMap.set(
              providedEdgeId,
              createEdge(
                currentTreeNode,
                currentTreeNode.content.slotProvidedStep,
                `${currentTreeNode.id}-slot-provided-handle`
              )
            );
          }

          currentTreeNode.content.slotConditionals.forEach(
            (conditional, index) => {
              if (conditional.nextStep) {
                nodesToAdd.push(conditional.nextStep);
                const edgeId = `${currentTreeNode.id}-slot-conditional-${index}->${conditional.nextStep.id}`;
                edgeMap.set(
                  edgeId,
                  createEdge(
                    currentTreeNode,
                    conditional.nextStep,
                    `${currentTreeNode.id}-slot-conditional-handle-${index}`
                  )
                );
              }
            }
          );
        } else {
          nodeType = 'default';
        }

        const flowNode = createNode(currentTreeNode, nodeType, data);
        nodeMap.set(currentTreeNode.id, flowNode);
        intentAndFlowMap.set(currentTreeNode.id, {
          id: currentTreeNode.id,
          intentNode: currentTreeNode,
          flowNode
        });
      }

      // Also add any unconnected nodes (ensuring they're only added once)
      unconnectedNodes.forEach((node) => {
        if (!nodeMap.has(node.id)) {
          const nodeType = getNodeType(node.content);
          const nodeData: any = {
            node,
            onNodeUpdate,
            onNodeDelete,
            isLocked
          };
          // Add onSlotValueClick for CollectVariable nodes
          if (node.content instanceof CollectVariableTreeNode) {
            nodeData.onSlotValueClick = onSlotValueClick;
          }
          // Add onOutcomeClick for CodeBlock nodes
          if (node.content instanceof CodeBlockTreeNode) {
            nodeData.onOutcomeClick = onSlotValueClick;
          }
          const flowNode = createNode(node, nodeType, nodeData);
          nodeMap.set(node.id, flowNode);
          intentAndFlowMap.set(node.id, {
            id: node.id,
            intentNode: node,
            flowNode
          });

          // Process connections within unconnected nodes to preserve internal structure
          if (node.content instanceof IntentTreeNode) {
            const intentContent = node.content as IntentTreeNode;
            if (
              intentContent.nextStep &&
              unconnectedNodes.some((n) => n.id === intentContent.nextStep!.id)
            ) {
              const edgeId = `${node.id}->${intentContent.nextStep.id}`;
              edgeMap.set(edgeId, createEdge(node, intentContent.nextStep));
            }
          } else if (node.content instanceof GenerativeAiTreeNode) {
            const generativeAiContent = node.content as GenerativeAiTreeNode;
            if (
              generativeAiContent.nextStep &&
              unconnectedNodes.some(
                (n) => n.id === generativeAiContent.nextStep!.id
              )
            ) {
              const edgeId = `${node.id}->${generativeAiContent.nextStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(node, generativeAiContent.nextStep)
              );
            }
          } else if (node.content instanceof CalculationTreeNode) {
            const calculationContent = node.content as CalculationTreeNode;
            if (
              calculationContent.nextStep &&
              unconnectedNodes.some(
                (n) => n.id === calculationContent.nextStep!.id
              )
            ) {
              const edgeId = `${node.id}->${calculationContent.nextStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(node, calculationContent.nextStep)
              );
            }
          } else if (node.content instanceof SendEmailTreeNode) {
            const sendEmailContent = node.content as SendEmailTreeNode;
            if (
              sendEmailContent.nextStep &&
              unconnectedNodes.some(
                (n) => n.id === sendEmailContent.nextStep!.id
              )
            ) {
              const edgeId = `${node.id}->${sendEmailContent.nextStep.id}`;
              edgeMap.set(edgeId, createEdge(node, sendEmailContent.nextStep));
            }
          } else if (node.content instanceof SendSmsTreeNode) {
            const sendSmsContent = node.content as SendSmsTreeNode;
            if (
              sendSmsContent.nextStep &&
              unconnectedNodes.some((n) => n.id === sendSmsContent.nextStep!.id)
            ) {
              const edgeId = `${node.id}->${sendSmsContent.nextStep.id}`;
              edgeMap.set(edgeId, createEdge(node, sendSmsContent.nextStep));
            }
          } else if (node.content instanceof DeleteVariableTreeNode) {
            const deleteVariableContent =
              node.content as DeleteVariableTreeNode;
            if (
              deleteVariableContent.nextStep &&
              unconnectedNodes.some(
                (n) => n.id === deleteVariableContent.nextStep!.id
              )
            ) {
              const edgeId = `${node.id}->${deleteVariableContent.nextStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(node, deleteVariableContent.nextStep)
              );
            }
          } else if (node.content instanceof LlmTreeNode) {
            const llmContent = node.content as LlmTreeNode;
            if (
              llmContent.nextStep &&
              unconnectedNodes.some((n) => n.id === llmContent.nextStep!.id)
            ) {
              const edgeId = `${node.id}->${llmContent.nextStep.id}`;
              edgeMap.set(edgeId, createEdge(node, llmContent.nextStep));
            }
          } else if (node.content instanceof CodeBlockTreeNode) {
            const codeBlockContent = node.content as CodeBlockTreeNode;
            if (
              codeBlockContent.nextStep &&
              unconnectedNodes.some(
                (n) => n.id === codeBlockContent.nextStep!.id
              )
            ) {
              const edgeId = `${node.id}-success->${codeBlockContent.nextStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(
                  node,
                  codeBlockContent.nextStep,
                  `${node.id}-success-handle`
                )
              );
            }
            if (
              codeBlockContent.nextStepOnError &&
              unconnectedNodes.some(
                (n) => n.id === codeBlockContent.nextStepOnError!.id
              )
            ) {
              const edgeId = `${node.id}-error->${codeBlockContent.nextStepOnError.id}`;
              edgeMap.set(
                edgeId,
                createEdge(
                  node,
                  codeBlockContent.nextStepOnError,
                  `${node.id}-error-handle`,
                  true
                )
              );
            }
          } else if (node.content instanceof MessageBlockTreeNode) {
            const messageContent = node.content as MessageBlockTreeNode;
            if (
              messageContent.nextStep &&
              unconnectedNodes.some((n) => n.id === messageContent.nextStep!.id)
            ) {
              const edgeId = `${node.id}->${messageContent.nextStep.id}`;
              edgeMap.set(edgeId, createEdge(node, messageContent.nextStep));
            }
          } else if (node.content instanceof ResponseCardsTreeNode) {
            const responseCardsContent = node.content as ResponseCardsTreeNode;
            responseCardsContent.items.forEach((item, index) => {
              if (
                item.nextStep &&
                unconnectedNodes.some((n) => n.id === item.nextStep!.id)
              ) {
                const edgeId = `${node.id}-response-card-${index}->${item.nextStep.id}`;
                edgeMap.set(
                  edgeId,
                  createEdge(
                    node,
                    item.nextStep,
                    `${node.id}-response-card-handle-${index}`
                  )
                );
              }
            });
          } else if (node.content instanceof CollectVariableTreeNode) {
            const collectVariableContent =
              node.content as CollectVariableTreeNode;
            if (
              collectVariableContent.fallbackStep &&
              unconnectedNodes.some(
                (n) => n.id === collectVariableContent.fallbackStep!.id
              )
            ) {
              const edgeId = `${node.id}-fallback->${collectVariableContent.fallbackStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(
                  node,
                  collectVariableContent.fallbackStep,
                  `${node.id}-fallback-handle`,
                  true
                )
              );
            }
            if (
              collectVariableContent.slotProvidedStep &&
              unconnectedNodes.some(
                (n) => n.id === collectVariableContent.slotProvidedStep!.id
              )
            ) {
              const edgeId = `${node.id}-slot-provided->${collectVariableContent.slotProvidedStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(
                  node,
                  collectVariableContent.slotProvidedStep,
                  `${node.id}-slot-provided-handle`
                )
              );
            }
            collectVariableContent.slotConditionals.forEach(
              (conditional, index) => {
                if (
                  conditional.nextStep &&
                  unconnectedNodes.some(
                    (n) => n.id === conditional.nextStep!.id
                  )
                ) {
                  const edgeId = `${node.id}-slot-conditional-${index}->${conditional.nextStep.id}`;
                  edgeMap.set(
                    edgeId,
                    createEdge(
                      node,
                      conditional.nextStep,
                      `${node.id}-slot-conditional-handle-${index}`
                    )
                  );
                }
              }
            );
          } else if (node.content instanceof ConditionalTreeNode) {
            const conditionalContent = node.content as ConditionalTreeNode;
            if (
              conditionalContent.trueStep &&
              unconnectedNodes.some(
                (n) => n.id === conditionalContent.trueStep!.id
              )
            ) {
              const edgeId = `${node.id}-true->${conditionalContent.trueStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(
                  node,
                  conditionalContent.trueStep,
                  `${node.id}-true-handle`
                )
              );
            }
            if (
              conditionalContent.falseStep &&
              unconnectedNodes.some(
                (n) => n.id === conditionalContent.falseStep!.id
              )
            ) {
              const edgeId = `${node.id}-false->${conditionalContent.falseStep.id}`;
              edgeMap.set(
                edgeId,
                createEdge(
                  node,
                  conditionalContent.falseStep,
                  `${node.id}-false-handle`
                )
              );
            }
          }
        }
      });

      const newEdges = Array.from(edgeMap.values());
      setNodes(Array.from(nodeMap.values()));
      setIntentAndFlowNodes(Array.from(intentAndFlowMap.values()));
      setEdges(newEdges);

      // Re-apply highlighting if it was active before nodes/edges were rebuilt
      reapplyHighlighting(newEdges);
    }
  }, [intent, unconnectedNodes, reapplyHighlighting, isLocked]);

  // Reset viewport when switching between intents
  useLayoutEffect(() => {
    if (reactFlowInstance && intent) {
      const currentIntentId = intent.id;
      const hadPreviousIntent = previousIntentIdRef.current !== null;
      const intentChanged = previousIntentIdRef.current !== currentIntentId;

      if (hadPreviousIntent && intentChanged && nodes.length > 0) {
        isFittingViewInProgressRef.current = true;
        setIsFittingView(true);

        const tryFitView = (attempt = 1, maxAttempts = 10) => {
          const rfNodes = reactFlowInstance.getNodes();
          const areNodesInitialized =
            rfNodes.length > 0 && rfNodes.every((n) => n.width && n.height);

          if (areNodesInitialized) {
            reactFlowInstance.fitView({
              minZoom: 0.1,
              maxZoom: 1.5
            });

            setTimeout(() => {
              isFittingViewInProgressRef.current = false;
              setIsFittingView(false);
            }, 250);
          } else if (attempt < maxAttempts) {
            setTimeout(() => tryFitView(attempt + 1, maxAttempts), 50);
          } else {
            isFittingViewInProgressRef.current = false;
            setIsFittingView(false);
          }
        };

        setTimeout(() => tryFitView(), 50);
      } else if (!isFittingViewInProgressRef.current) {
        setIsFittingView(false);
      }

      previousIntentIdRef.current = currentIntentId;
    } else if (!isFittingViewInProgressRef.current) {
      setIsFittingView(false);
    }
  }, [intent?.id, reactFlowInstance, nodes.length]);

  const onNodesChange: OnNodesChange = useCallback(
    (changes) => {
      const selectionChanges = changes.filter(
        (change) => change.type === 'select'
      ) as NodeSelectionChange[];

      if (selectionChanges.length > 0) {
        const selectedNodes = selectionChanges.filter((c) => c.selected);
        if (selectedNodes.length > 0) {
          const selectedIntentNode =
            intentAndFlowNodes.find((n) => n.id === selectedNodes[0].id)
              ?.intentNode || null;
          setSelectedNode(selectedIntentNode);
          onSelectedNodeChange(selectedIntentNode);
        } else {
          setSelectedNode(null);
          onSelectedNodeChange(null);
        }
      }

      changes.forEach((change) => {
        if (change.type === 'position' && change.position) {
          const nodeId = change.id;

          const intentNodeAndFlowNode = intentAndFlowNodes.find(
            (n) => n.id === nodeId
          );

          if (intentNodeAndFlowNode) {
            intentNodeAndFlowNode.intentNode.xPosition = change.position.x;
            intentNodeAndFlowNode.intentNode.yPosition = change.position.y;
          }
          setIntentHasChanged(true);
        }
      });

      setNodes((nds) => applyNodeChanges(changes, nds));
    },
    [intentAndFlowNodes, onSelectedNodeChange]
  );

  const onEdgesChange: OnEdgesChange = useCallback(
    (changes) => {
      changes.forEach((change) => {
        if (change.type === 'remove') {
          const removedId = change.id;
          const parts = removedId.split('->');
          if (parts.length !== 2) return;

          const leftPart = parts[0];
          const targetId = parts[1];

          const sourceNodeData = intentAndFlowNodes.find((n) =>
            leftPart.startsWith(n.id)
          );
          const targetNodeData = intentAndFlowNodes.find(
            (n) => n.id === targetId
          );

          if (sourceNodeData && targetNodeData) {
            const sourceNode = sourceNodeData.intentNode;
            const targetNode = targetNodeData.intentNode;

            if (
              sourceNode.content instanceof IntentTreeNode ||
              sourceNode.content instanceof MessageBlockTreeNode ||
              sourceNode.content instanceof GenerativeAiTreeNode ||
              sourceNode.content instanceof CalculationTreeNode ||
              sourceNode.content instanceof SendEmailTreeNode ||
              sourceNode.content instanceof SendSmsTreeNode ||
              sourceNode.content instanceof DeleteVariableTreeNode ||
              sourceNode.content instanceof LlmTreeNode
            ) {
              const expectedId = `${sourceNode.id}->${targetNode.id}`;
              if (removedId === expectedId) {
                if (sourceNode.content.nextStep?.id === targetNode.id) {
                  sourceNode.content.nextStep = null;
                  onNodeUpdate(sourceNode);
                }
              }
            } else if (sourceNode.content instanceof CodeBlockTreeNode) {
              if (removedId.includes('success')) {
                if (sourceNode.content.nextStep?.id === targetNode.id) {
                  sourceNode.content.nextStep = null;
                  onNodeUpdate(sourceNode);
                }
              } else if (removedId.includes('error')) {
                if (sourceNode.content.nextStepOnError?.id === targetNode.id) {
                  sourceNode.content.nextStepOnError = null;
                  onNodeUpdate(sourceNode);
                }
              }
            } else if (sourceNode.content instanceof CollectVariableTreeNode) {
              if (removedId.includes('fallback')) {
                if (sourceNode.content.fallbackStep?.id === targetNode.id) {
                  sourceNode.content.fallbackStep = null;
                  onNodeUpdate(sourceNode);
                }
              } else if (removedId.includes('slot-provided')) {
                if (sourceNode.content.slotProvidedStep?.id === targetNode.id) {
                  sourceNode.content.slotProvidedStep = null;
                  onNodeUpdate(sourceNode);
                }
              } else if (removedId.includes('slot-conditional')) {
                const regex = /slot-conditional-handle-(\d+)$/;
                const match = leftPart.match(regex);
                if (match) {
                  const index = parseInt(match[1], 10);
                  if (
                    !Number.isNaN(index) &&
                    sourceNode.content.slotConditionals[index] &&
                    sourceNode.content.slotConditionals[index].nextStep?.id ===
                      targetNode.id
                  ) {
                    sourceNode.content.slotConditionals[index].nextStep = null;
                    onNodeUpdate(sourceNode);
                  }
                }
              }
            } else if (sourceNode.content instanceof ResponseCardsTreeNode) {
              const regex = /response-card-handle-(\d+)/;
              const match = leftPart.match(regex);
              if (match) {
                const index = parseInt(match[1], 10);
                if (
                  !Number.isNaN(index) &&
                  sourceNode.content.items[index] &&
                  sourceNode.content.items[index].nextStep?.id === targetNode.id
                ) {
                  sourceNode.content.items[index].nextStep = null;
                  onNodeUpdate(sourceNode);
                }
              }
            } else if (sourceNode.content instanceof ConditionalTreeNode) {
              if (removedId.includes('true')) {
                if (sourceNode.content.trueStep?.id === targetNode.id) {
                  sourceNode.content.trueStep = null;
                  onNodeUpdate(sourceNode);
                }
              } else if (removedId.includes('false')) {
                if (sourceNode.content.falseStep?.id === targetNode.id) {
                  sourceNode.content.falseStep = null;
                  onNodeUpdate(sourceNode);
                }
              }
            }
          }
        }
      });

      setEdges((eds) => applyEdgeChanges(changes, eds));

      promoteOrphansToUnconnected();
    },
    [intentAndFlowNodes, onNodeUpdate, setEdges, promoteOrphansToUnconnected]
  );

  const onConnect: OnConnect = useCallback(
    (connection) => {
      const sourceNode = intentAndFlowNodes.find(
        (n) => n.id === connection.source
      )?.intentNode;
      const targetNode = intentAndFlowNodes.find(
        (n) => n.id === connection.target
      )?.intentNode;

      if (!sourceNode || !targetNode) return;

      const isSelfReference = sourceNode.id === targetNode.id;

      if (isSelfReference) {
        if (
          sourceNode.content instanceof CollectVariableTreeNode &&
          connection.sourceHandle?.includes('fallback')
        ) {
          setEdges((eds) => {
            const filteredEdges = eds.filter(
              (edge) =>
                !(
                  edge.source === connection.source &&
                  edge.sourceHandle === connection.sourceHandle
                )
            );
            return addEdge(connection, filteredEdges);
          });

          sourceNode.content.fallbackStep = sourceNode;
          onNodeUpdate(sourceNode);
          return;
        }
        openErrorSnackbar(
          'Self-reference is only allowed for fallback handles'
        );
        return;
      }

      if (
        sourceNode.content instanceof CollectVariableTreeNode &&
        targetNode.content instanceof ResponseCardsTreeNode
      ) {
        openErrorSnackbar(
          'Cannot connect Collect variable to Follow up options'
        );
        return;
      }

      setEdges((eds) => {
        const filteredEdges = eds.filter((edge) => {
          if (connection.sourceHandle) {
            return !(
              edge.source === connection.source &&
              edge.sourceHandle === connection.sourceHandle
            );
          }
          return edge.source !== connection.source;
        });

        return addEdge(connection, filteredEdges);
      });

      if (sourceNode.content instanceof IntentTreeNode) {
        sourceNode.content.nextStep = targetNode;
        onNodeUpdate(sourceNode);
      } else if (sourceNode.content instanceof MessageBlockTreeNode) {
        sourceNode.content.nextStep = targetNode;
        onNodeUpdate(sourceNode);
      } else if (
        sourceNode.content instanceof GenerativeAiTreeNode ||
        sourceNode.content instanceof CalculationTreeNode ||
        sourceNode.content instanceof SendEmailTreeNode ||
        sourceNode.content instanceof SendSmsTreeNode ||
        sourceNode.content instanceof DeleteVariableTreeNode ||
        sourceNode.content instanceof LlmTreeNode
      ) {
        sourceNode.content.nextStep = targetNode;
        onNodeUpdate(sourceNode);
      } else if (sourceNode.content instanceof CodeBlockTreeNode) {
        if (connection.sourceHandle?.includes('success')) {
          sourceNode.content.nextStep = targetNode;
        } else if (connection.sourceHandle?.includes('error')) {
          sourceNode.content.nextStepOnError = targetNode;
        }
        onNodeUpdate(sourceNode);
      } else if (sourceNode.content instanceof ResponseCardsTreeNode) {
        if (connection.sourceHandle) {
          const parts = connection.sourceHandle.split('-');
          const indexStr = parts[parts.length - 1];
          const index = parseInt(indexStr, 10);
          if (!Number.isNaN(index) && sourceNode.content.items[index]) {
            sourceNode.content.items[index].nextStep = targetNode;
            onNodeUpdate(sourceNode);
          }
        }
      } else if (sourceNode.content instanceof CollectVariableTreeNode) {
        if (connection.sourceHandle === `${sourceNode.id}-fallback-handle`) {
          sourceNode.content.fallbackStep = targetNode;
          onNodeUpdate(sourceNode);
        } else if (
          connection.sourceHandle === `${sourceNode.id}-slot-provided-handle`
        ) {
          sourceNode.content.slotProvidedStep = targetNode;
          onNodeUpdate(sourceNode);
        } else if (connection.sourceHandle?.includes('slot-conditional')) {
          const parts = connection.sourceHandle.split('-');
          const indexStr = parts[parts.length - 1];
          const index = parseInt(indexStr, 10);
          if (
            !Number.isNaN(index) &&
            sourceNode.content.slotConditionals &&
            sourceNode.content.slotConditionals[index]
          ) {
            sourceNode.content.slotConditionals[index].nextStep = targetNode;
            onNodeUpdate(sourceNode);
          }
        }
      } else if (sourceNode.content instanceof ConditionalTreeNode) {
        if (connection.sourceHandle === `${sourceNode.id}-true-handle`) {
          sourceNode.content.trueStep = targetNode;
        } else if (
          connection.sourceHandle === `${sourceNode.id}-false-handle`
        ) {
          sourceNode.content.falseStep = targetNode;
        }
        onNodeUpdate(sourceNode);
      }

      // Only remove target node from unconnectedNodes if it's now connected to the root
      const rootNode = intentAndFlowNodes.find(
        (n) => n.intentNode.content instanceof IntentTreeNode
      )?.intentNode;

      if (rootNode && isNodeReachableFromRoot(targetNode.id, rootNode)) {
        removeNode(targetNode);
      }

      promoteOrphansToUnconnected();
    },
    [
      intentAndFlowNodes,
      onNodeUpdate,
      removeNode,
      isNodeReachableFromRoot,
      promoteOrphansToUnconnected
    ]
  );

  const flowProOptions = { hideAttribution: true };

  const [isDragOver, setIsDragOver] = useState(false);
  const [paletteExpanded, setPaletteExpanded] = useState(true);

  const createDefaultMessageBlockContent = (): MessageBlockContent => ({
    content: null,
    contentType: 'MARKDOWN',
    blockOrder: 0,
    id: uuidv4()
  });

  const createNodeFromType = (
    nodeType: string,
    position: { x: number; y: number }
  ): BotResponseTreeNode => {
    switch (nodeType) {
      case 'response':
        return {
          id: uuidv4(),
          content: new MessageBlockTreeNode(
            [createDefaultMessageBlockContent()],
            null,
            null,
            undefined,
            undefined
          ),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'responseCard':
        return {
          id: uuidv4(),
          content: new ResponseCardsTreeNode([]),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'goToIntent':
        return {
          id: uuidv4(),
          content: new GoToIntentTreeNode(null, null),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'collectVariable':
        return {
          id: uuidv4(),
          content: new CollectVariableTreeNode(
            null,
            null,
            null,
            SlotValueType.ENUMERATED_VALUE,
            null,
            null,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined
          ),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'conditional':
        return {
          id: uuidv4(),
          content: new ConditionalTreeNode(
            null,
            null,
            null,
            ComparisonOperator.EQUALS,
            null,
            null,
            null
          ),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'generativeAi':
        return {
          id: uuidv4(),
          content: new GenerativeAiTreeNode(null),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'calculation':
        return {
          id: uuidv4(),
          content: new CalculationTreeNode(null),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'sendEmail':
        return {
          id: uuidv4(),
          content: new SendEmailTreeNode('', '', '', null, null, []),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'sendSms':
        return {
          id: uuidv4(),
          content: new SendSmsTreeNode('', '', null, []),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'deleteVariable':
        return {
          id: uuidv4(),
          content: new DeleteVariableTreeNode('', null),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'codeBlock':
        return {
          id: uuidv4(),
          content: new CodeBlockTreeNode('', [], [], null),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'llm':
        return {
          id: uuidv4(),
          content: new LlmTreeNode(
            '',
            '',
            DEFAULT_AI_MODEL,
            0.25,
            null,
            '',
            SlotValueType.FREE_TEXT
          ),
          xPosition: position.x,
          yPosition: position.y
        };
      case 'doNothing':
        return {
          id: uuidv4(),
          content: new DoNothingTreeNode(),
          xPosition: position.x,
          yPosition: position.y
        };
      default:
        throw new Error(`Unknown node type: ${nodeType}`);
    }
  };

  const handleCreateNodeFromKeyboard = useCallback(
    (nodeType: string, position: { x: number; y: number }) => {
      const typeMap: { [key: string]: string } = {
        respondToUser: 'response',
        collectVariable: 'collectVariable',
        followUp: 'responseCard',
        goToQuestion: 'goToIntent',
        variableComparison: 'conditional'
      };

      const internalNodeType = typeMap[nodeType];
      if (!internalNodeType) return;

      const newNode = createNodeFromType(internalNodeType, position);
      addNode(newNode);
      onNodeUpdate(newNode);
    },
    [addNode, onNodeUpdate]
  );

  const handleDeleteSelectedNode = useCallback(() => {
    if (selectedNode) {
      if (selectedNode.content instanceof IntentTreeNode) {
        return;
      }
      onNodeDelete(selectedNode);
      setSelectedNode(null);
    }
  }, [selectedNode, onNodeDelete]);

  const handleDeleteSelectedEdge = useCallback(() => {
    const selectedEdges = edges.filter((edge) => edge.selected);
    if (selectedEdges.length > 0) {
      const edgeChanges = selectedEdges.map((edge) => ({
        id: edge.id,
        type: 'remove' as const
      }));
      onEdgesChange(edgeChanges);
      return true;
    }
    return false;
  }, [edges, onEdgesChange]);

  const handleEscape = useCallback(() => {
    setSelectedNode(null);
    onSelectedNodeChange(null);
    resetHighlighting();
    setIsPreviewMode(true);
  }, [onSelectedNodeChange, resetHighlighting, setIsPreviewMode]);

  useKeyboardShortcuts({
    onCreateNode: handleCreateNodeFromKeyboard,
    onDeleteNode: handleDeleteSelectedNode,
    onDeleteEdge: handleDeleteSelectedEdge,
    onEscape: handleEscape
  });

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const { dataTransfer } = event;
    if (dataTransfer) {
      dataTransfer.dropEffect = 'move';
    }
    if (!isDragOver) {
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const { clientX, clientY } = event;

    if (
      clientX < rect.left ||
      clientX >= rect.right ||
      clientY < rect.top ||
      clientY >= rect.bottom
    ) {
      setIsDragOver(false);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragOver(false);

    const { dataTransfer } = event;
    const nodeType = dataTransfer
      ? dataTransfer.getData('application/reactflow')
      : '';
    if (!nodeType || !reactFlowInstance) return;

    const reactFlowElement = event.currentTarget.querySelector('.react-flow');
    if (!reactFlowElement) return;

    const bounds = reactFlowElement.getBoundingClientRect();
    const position = reactFlowInstance.project({
      x: event.clientX - bounds.left,
      y: event.clientY - bounds.top
    });

    const centeredPosition = {
      x: position.x - 224,
      y: position.y - 60
    };

    const newNode = createNodeFromType(nodeType, centeredPosition);
    addNode(newNode);
    onNodeUpdate(newNode);
  };

  const handleCanvasClick = useCallback(() => {
    onSelectedNodeChange(null);
    resetHighlighting();
    setIsPreviewMode(true);
  }, [onSelectedNodeChange, resetHighlighting, setIsPreviewMode]);

  const handleNodeClick = useCallback(
    (_event: React.MouseEvent, node: Node) => {
      const isUnconnected = unconnectedNodes.some(
        (unconnectedNode) => unconnectedNode.id === node.data?.node?.id
      );

      if (isUnconnected) {
        resetHighlighting();
        return;
      }

      highlightParent(node.id, edges);

      const clickedIntentNode =
        intentAndFlowNodes.find((n) => n.id === node.id)?.intentNode || null;
      if (clickedIntentNode) {
        onSelectedNodeChange(clickedIntentNode);
      }
    },
    [
      unconnectedNodes,
      highlightParent,
      resetHighlighting,
      edges,
      intentAndFlowNodes,
      onSelectedNodeChange
    ]
  );

  const handleSlotValueClick = useCallback(
    (nodeId: string, handleId: string) => {
      highlightChild(nodeId, handleId, edges);
    },
    [highlightChild, edges]
  );

  const handleHighlight = useCallback(
    (nodeId: string) => {
      const isUnconnected = unconnectedNodes.some(
        (unconnectedNode) => unconnectedNode.id === nodeId
      );

      if (isUnconnected) {
        resetHighlighting();
        return;
      }

      highlightParent(nodeId, edges);
    },
    [unconnectedNodes, highlightParent, resetHighlighting, edges]
  );

  // Keep the refs updated with the latest handlers
  slotValueClickRef.current = handleSlotValueClick;
  highlightRef.current = handleHighlight;

  return (
    <div className='flex relative h-[calc(100vh-5rem)] w-full'>
      <div
        className='reactflow-wrapper bg-white relative overflow-auto flex-1 h-full'
        onDragOver={isLocked ? undefined : handleDragOver}
        onDragLeave={isLocked ? undefined : handleDragLeave}
        onDrop={isLocked ? undefined : handleDrop}
        role='application'
        aria-label='Bot builder canvas - drag and drop nodes here'
      >
        {isLocked && (
          <div className='absolute top-4 right-4 z-[10]'>
            <CustomTooltip content='This question is locked by a Japeto admin'>
              <div className='flex items-center gap-2 bg-gray-100 text-gray-600 px-3 py-1.5 rounded-full border border-gray-200'>
                <Lock size={14} />
                <span className='text-sm font-medium'>Locked</span>
              </div>
            </CustomTooltip>
          </div>
        )}
        {(newIntentIsLoading || isFittingView) && (
          <div className='absolute top-0 left-0 z-[2] bg-white h-full w-full flex items-center justify-center'>
            <Loader2 className='h-12 w-12 animate-spin text-primary' />
          </div>
        )}
        <ReactFlow
          onInit={setReactFlowInstance}
          nodes={nodes}
          className='h-full w-full z-[1]'
          style={{
            backgroundColor: isDragOver
              ? 'rgba(0, 46, 95, 0.05)'
              : 'transparent'
          }}
          onNodesChange={onNodesChange}
          edges={edges}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          proOptions={flowProOptions}
          connectionLineType={ConnectionLineType.SmoothStep}
          fitView
          minZoom={0.1}
          maxZoom={2}
          connectionRadius={40}
          onPaneClick={handleCanvasClick}
          onNodeClick={handleNodeClick}
          deleteKeyCode={[]}
        >
          <Background />
          <Controls position='bottom-right'>
            <KeyboardShortcuts />
          </Controls>
        </ReactFlow>
      </div>
      <NodePalette onExpandedChange={setPaletteExpanded} isLocked={isLocked} />
    </div>
  );
}

export default BotBuilder;
